import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { ClientItemId } from 'owa-client-ids';
import loc from 'owa-localize';
import { findInlineComposeViewState, lazyOpenCompose } from 'owa-mail-compose-actions';
import { TableQueryType } from 'owa-mail-list-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyReplyToConversation } from 'owa-mail-message-actions';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';
import getLastestNonDraftItemId from 'owa-mail-reading-pane-store/lib/utils/getLastestNonDraftItemId';
import { SenderImage } from 'owa-mail-sender-persona-view';
import mailStore from 'owa-mail-store/lib/store/Store';
import isReplyToAll from 'owa-readonly-recipient-well/lib/utils/isReplyToAll';
import Message from 'owa-service/lib/contract/Message';
import * as React from 'react';
import {
    globalReplyToAllLabel,
    globalReplyLabel as globalReplyLabel_1,
} from './GlobalReplyButton.locstring.json';
import {
    shouldSuppressServerMarkReadOnReplyOrForward,
    getSelectedTableView,
} from 'owa-mail-list-store';

const styles = require('./ConversationReadingPane.scss');
const classNames = require('classnames/bind').bind(styles);

export interface GlobalReplyButtonProps {
    conversationId: ClientItemId;
    handleGlobalReplyClicked: () => void;
    isFocused: boolean;
}

@observer
export default class GlobalReplyButton extends React.Component<GlobalReplyButtonProps, {}> {
    private isReplyAllAllowed: boolean;
    private globalReplyContainer: HTMLDivElement;

    public componentDidUpdate(prevProps: GlobalReplyButtonProps) {
        if (this.globalReplyContainer && !prevProps.isFocused && this.props.isFocused) {
            this.globalReplyContainer.focus();
        }
    }

    render() {
        const { conversationId, isFocused } = this.props;
        const globalReplyButtonClassNames = classNames(styles.iconButton, styles.globalReplyButton);
        const latestNonDraftItemId = getLastestNonDraftItemId(conversationId);
        const message =
            latestNonDraftItemId && (mailStore.items.get(latestNonDraftItemId.Id) as Message);
        this.isReplyAllAllowed = !checkItemReplyForwardDisabled(
            message,
            true /* isConversationItemPart */
        )[1]; /* isReplyAllDisabled */
        const globalReplyButtonTextClassNames = classNames(
            styles.globalReplyButtonText,
            this.isReplyAllAllowed ? '' : styles.disabledGlobalReplyButtonText
        );
        const globalReplyLabel =
            latestNonDraftItemId && isReplyToAll(message)
                ? loc(globalReplyToAllLabel)
                : loc(globalReplyLabel_1);

        const isComposing = findInlineComposeViewState(conversationId.Id);
        const importOpenCompose = () => {
            lazyOpenCompose.import();
        };

        return !isComposing && latestNonDraftItemId ? (
            <div
                ref={this.setGlobalReplyContainer}
                tabIndex={-1}
                className={classNames(styles.globalReplyBox, { isFocused: isFocused })}
                onMouseOver={importOpenCompose}>
                <IconButton
                    onClick={this.onReplyAll}
                    className={globalReplyButtonClassNames}
                    disabled={!this.isReplyAllAllowed}
                    styles={{
                        rootDisabled: styles.disabledReplyActionButton,
                    }}>
                    <span className={globalReplyButtonTextClassNames}>{globalReplyLabel}</span>
                </IconButton>
                <SenderImage
                    sender={null}
                    displaySelf={true}
                    style={styles.globalReplySenderImage}
                />
            </div>
        ) : null;
    }

    private setGlobalReplyContainer = (ref: HTMLDivElement) => {
        this.globalReplyContainer = ref;
    };

    public onReplyAll = (event?: React.MouseEvent<unknown>) => {
        if (!this.isReplyAllAllowed) {
            return;
        }

        event && event.stopPropagation();
        this.props.handleGlobalReplyClicked();

        let instrumentationContexts;
        const tableView = getSelectedTableView();
        if (tableView && tableView.tableQuery.type == TableQueryType.Search) {
            const selectedRows = [...tableView.selectedRowKeys.keys()];
            instrumentationContexts = getInstrumentationContextsFromTableView(
                selectedRows,
                tableView
            );
        }
        lazyReplyToConversation.importAndExecute(
            this.props.conversationId,
            true /*isReplyAll*/,
            'ReadingPane',
            instrumentationContexts,
            shouldSuppressServerMarkReadOnReplyOrForward(tableView)
        );
    };
}
